import { WizardStepTitle } from "app/modules/shared/wizard/WizardStepTitle";
import { ServiceChargeInvoicingSteps } from "../../ServiceChargeInvoiceWizardSteps";
import { faArrowLeft, faEye, faFileInvoice } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WizardStepperFooter } from "app/modules/shared/wizard/WizardStepFooter";
import { useDispatch, useSelector } from "react-redux";
import {
  getServiceChargeInvoiceProcessSelectionModel,
  selectServiceChargeInvoice,
  setCurrentStep,
  setServiceChargeInvoiceModel,
} from "../../ServiceChargeInvoicingSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  acceptInvoices,
  generateInvoiceModelsBackgroundProcess,
  getPreviewURL,
  selectGeneratingInvoices,
  selectProgress,
  selectRunGenerateInvoicePreviews,
} from "./InvoicesPreviewSlice";
import { useEffect, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import { setIsSavingInvoices } from "../InvoiceSending/InvoiceSendingSlice";

const InvoicesPreview: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const runGeneratePreviews = useSelector(selectRunGenerateInvoicePreviews);
  const generatingInvoicePreviews = useSelector(selectGeneratingInvoices);
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const serviceChargeInvoice = useSelector(selectServiceChargeInvoice);
  const progress = useSelector(selectProgress);

  useEffect(() => {
    if (runGeneratePreviews) {
      thunkDispatch(generateInvoiceModelsBackgroundProcess());
    }
  }, [runGeneratePreviews, thunkDispatch]);

  useEffect(() => {
    if (!runGeneratePreviews && !generatingInvoicePreviews)
      thunkDispatch(getServiceChargeInvoiceProcessSelectionModel());
  }, [runGeneratePreviews, generatingInvoicePreviews, thunkDispatch]);

  function goToPreviousStep() {
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.DevelopmentChargesPreList));
    dispatch(
      setServiceChargeInvoiceModel({
        ...serviceChargeInvoice,
        invoicereportmodels: undefined,
      })
    );
  }

  async function previewInvoices() {
    const newWindow = window.open("", "_blank");

    if (!newWindow) {
      throw new Error("Unable to open new window");
    }

    try {
      const previewURL = await thunkDispatch(getPreviewURL()).unwrap();

      if (!previewURL) {
        newWindow.close();
        return;
      }

      newWindow.location.href = previewURL;
      newWindow.focus();
      setHasPreviewed(true);
    } catch (err) {
      newWindow.close();
    }
  }

  function acceptInvoicesClick() {
    thunkDispatch(acceptInvoices());
    dispatch(setIsSavingInvoices(true));
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.SendInvoices));
  }

  return (
    <>
      <WizardStepTitle
        stepNumber={ServiceChargeInvoicingSteps.GenerateInvoices}
        title={"Invoice Previews"}
        subtitle={"Please review the invoices and click 'Accept Invoices' when you're ready to proceed."}
      ></WizardStepTitle>
      <div>
        {generatingInvoicePreviews ? (
          <>
            <ProgressBar min={0} now={progress} max={100} variant="success" striped animated />
            <div className="d-flex justify-content-center">
              <div className="d-flex flex-column align-items-center me-2">
                <p>The invoice previews are being generated and your progress has been saved.</p>
                <p>
                  You can navigate away at any time and check the status of the preview generation in Background
                  Activities.
                </p>
                <p>A notification will be sent once the process is complete.</p>
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex justify-content-center">
            <div className="d-flex align-items-center me-2 gap-2">
              <p>
                Your invoices have been generated.
                <br />
                Clicking the preview button will open a new window <br />
                allowing you to confirm the details are correct.
              </p>
              <button
                data-test-id="invoice-preview-step__preview-invoices-button"
                className="btn btn-warning btn-sm"
                type="button"
                onClick={() => previewInvoices()}
                disabled={generatingInvoicePreviews}
              >
                <FontAwesomeIcon icon={faEye} />
                <span className="ms-1">Preview Invoices</span>
              </button>
            </div>
          </div>
        )}
      </div>
      <WizardStepperFooter>
        <div className="d-flex justify-content-between">
          <div>
            <button
              data-test-id="invoice-preview-step__go-back-button"
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => goToPreviousStep()}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="ms-1">Go back</span>
            </button>
          </div>
          <div>
            <button
              data-test-id="invoice-preview-step__accept-invoices-button"
              className="btn btn-success btn-sm"
              type="button"
              onClick={() => acceptInvoicesClick()}
              disabled={!hasPreviewed}
            >
              <span className="me-1">Accept Invoices</span>
              <FontAwesomeIcon icon={faFileInvoice} />
            </button>
          </div>
        </div>
      </WizardStepperFooter>
    </>
  );
};

export default InvoicesPreview;
